'use client';
import { Box, IconButton, Modal } from '@mui/material';
import classNames from 'classnames';
import { useRef, useState } from 'react';
import { type FC } from 'react';

import { CloseIconWhite, PlayIcon, PlayIconBudget } from '@abg/kit-base';
import { usePageContext } from '@abg/web-core/context/page/client-provider';

import { AemImage } from '../AemImage';

import { vimeoBaseUrl, youtubeBaseUrl } from './video-player.constants';
import { getVideoIdFromUrl } from './video-player.helper';
import {
  CloseIconStyle,
  ContainerStyle,
  PlayButtontnStyle,
  VideoContainerStyle,
  VideoPlayerPlayButtonStyle,
  VideoPlayerStyle,
} from './video-player.styling';
import { VideoPlayerProperties } from './video-player.types';

const ANALYTICS_YOUTUBE_CLASS = 'analytics-video-youtube';
const ANALYTICS_VIMEO_CLASS = 'analytics-video-vimeo';

export const VideoPlayer: FC<VideoPlayerProperties> = ({
  youtubeSourceUrl,
  videoSource,
  vimeoSourceUrl,
  damSourceUrl,
  videoThumnailImage,
  smartCropName,
}) => {
  const {
    config: {
      aem: { origin },
    },
    brand,
  } = usePageContext();

  const videoReference = useRef<HTMLVideoElement | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const uniqueId = getVideoIdFromUrl(
    videoSource == 'vimeo' ? vimeoSourceUrl : youtubeSourceUrl,
  );

  const handleThumbnailClick = () => {
    if (youtubeSourceUrl || vimeoSourceUrl) {
      // this event handles calls the script once if we have multiple youtube videos
      globalThis.dispatchEvent(new Event('load-youtube-iframe'));
    }
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    if (videoReference.current) {
      videoReference.current.pause(); // stop video when modal closes
    }
  };

  return (
    <Box sx={{ ...ContainerStyle }}>
      <AemImage
        data-testid="media-module-image"
        src={videoThumnailImage}
        alt={'Media Image'}
        fill
        smartCropName={smartCropName}
      />
      <IconButton
        sx={{
          ...VideoPlayerPlayButtonStyle,
        }}
        onClick={handleThumbnailClick}
        color="primary"
        className={classNames(
          youtubeSourceUrl && ANALYTICS_YOUTUBE_CLASS,
          vimeoSourceUrl && ANALYTICS_VIMEO_CLASS,
        )}
      >
        {brand == 'avis' ? (
          <PlayIcon
            sx={{
              ...PlayButtontnStyle,
            }}
          />
        ) : (
          <PlayIconBudget
            sx={{
              ...PlayButtontnStyle,
            }}
          />
        )}
      </IconButton>

      <Modal open={isModalOpen} onClose={handleClose}>
        <Box
          sx={{
            ...VideoContainerStyle,
          }}
        >
          <IconButton onClick={handleClose} sx={{ ...CloseIconStyle }}>
            <CloseIconWhite />
          </IconButton>

          {videoSource === 'assetLibrary' ? (
            <Box
              component="video"
              ref={videoReference}
              sx={{ ...VideoPlayerStyle }}
              controls
              autoPlay
            >
              <source src={`${origin}${damSourceUrl}`} type="video/mp4" />
              <track kind="captions" />
            </Box>
          ) : (
            <Box
              component="iframe"
              title="video"
              src={
                videoSource === 'vimeo'
                  ? `${vimeoBaseUrl}/${uniqueId}?autoplay=1`
                  : `${youtubeBaseUrl}/${uniqueId}?autoplay=1&enablejsapi=1`
              }
              allow="autoplay; fullscreen"
              allowFullScreen
              sx={{
                ...VideoPlayerStyle,
              }}
            />
          )}
        </Box>
      </Modal>
    </Box>
  );
};
